import { CgSpinner } from 'react-icons/cg';
import { MdOutlineErrorOutline } from 'react-icons/md';
import { IoIosCheckmarkCircleOutline } from 'react-icons/io';
import DestinationPartnerIcon from '../../components/icons/DestinationPartnerIcon';
import { useQuery } from '@apollo/client';
import { useParams } from 'react-router-dom';
import { ACTIVATION_QUERY } from '../../queries/activationQueries';
import LoadingSpinner from '../../components/LoadingSpinner';
import ErrorText from '../../components/ErrorText';
import { formatTimestamp } from './activationFormatters';
import { useEffect } from 'react';

export default function DistributionSummary() {
  const { activationId } = useParams();
  const { loading, error, data, startPolling, stopPolling } = useQuery(
    ACTIVATION_QUERY,
    {
      variables: { activationId },
      fetchPolicy: 'cache-and-network',
    }
  );

  useEffect(() => {
    if (['completed', 'failed'].includes(data?.activation.processingStatus)) {
      stopPolling();
    } else {
      startPolling(1000 * 5); // 5 seconds
    }

    return () => stopPolling();
  }, [data?.activation.processingStatus, stopPolling]);

  if (loading)
    return <LoadingSpinner message="Loading distribution details..." />;
  if (error) return <ErrorText message={error.message} />;
  if (!data?.activation) return <ErrorText message="Activation not found." />;

  const activation = data.activation;

  return (
    <div className="min-h-screen bg-gray-50 py-10 px-4">
      <div className="max-w-3xl mx-auto bg-white p-6 px-8 rounded-lg shadow-sm">
        {/* Header */}
        <div className="flex flex-col justify-start items-start gap-3">
          <DistributionStatus value={activation.processingStatus} />
          <h1 className="text-2xl font-bold text-gray-900 p-0 mb-2">
            Distribution Summary
          </h1>
        </div>

        {/* Top Fields: Audience & Distribution ID + Status */}
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Audience Name */}
          <div>
            <p className="text-sm text-gray-500">Audience Name</p>
            <p className="text-lg font-medium text-gray-800">
              {/* ATAD-132: audience and destinationPartner are nullable —
                  audiences are hard deleted, and activations created by the
                  old checkout flow never stored a destination ref. */}
              {activation.audience?.name ?? activation.audienceName ?? '—'}
            </p>
          </div>
          {/* Distribution ID + Status */}
          <div>
            <p className="text-sm text-gray-500">Distribution ID</p>
            <p className="text-lg font-medium text-gray-800 flex items-center space-x-2">
              <span>{activation.destinationPartner?.id ?? '—'}</span>
            </p>
          </div>
        </div>

        {/* Thank You / Intro Message */}
        <div className="mt-8">
          <h2 className="text-xl font-semibold text-gray-900">Thank you!</h2>
          <p className="text-sm text-gray-700 mt-1">
            We've received your distribution. Below are the details of your
            request.
          </p>
        </div>

        {/* Details Section */}
        <div className="mt-6 space-y-4">
          {/* Destination Partner */}
          <div>
            <p className="text-sm text-gray-500">Destination Partner</p>
            <div className="flex items-center space-x-2 mt-1">
              <DestinationPartnerIcon
                slug={activation.destinationPartner?.slug}
              />
              <span className="text-lg font-medium text-gray-800">
                {activation.destinationPartner?.name ??
                  activation.destinationPartnerName ??
                  '—'}
              </span>
            </div>
          </div>

          {/* LiveRamp Folder — only present when the destination is one of
              the seeded LiveRamp variants (carries `displayName`). Shows the
              same human-readable folder name we render in place of
              "Connected" on the Activations builder tile. */}
          {activation.destinationPartner?.displayName && (
            <div>
              <p className="text-sm text-gray-500">LiveRamp Folder</p>
              <p className="text-lg font-medium text-gray-800">
                {activation.destinationPartner?.displayName}
              </p>
            </div>
          )}

          {/* Percent of total audience */}
          <div>
            <p className="text-sm text-gray-500">Percent of total audience</p>
            <p className="text-lg font-medium text-gray-800">
              {activation.percentSelected * 100}%
            </p>
          </div>

          {/* Audience Sent */}
          <div>
            <p className="text-sm text-gray-500">Audience Sent</p>
            <p className="text-lg font-medium text-gray-800">
              {activation.recordsIncluded.toLocaleString()}
            </p>
          </div>

          {/* Number of Successful Records */}
          <div>
            <p className="text-sm text-gray-500">
              Number of Successful Records
            </p>
            <p className="text-lg font-medium text-gray-800">
              {(activation.recordsSuccessful || 0).toLocaleString()}
            </p>
          </div>

          {/* Number of Failed Records */}
          <div>
            <p className="text-sm text-gray-500">Number of Failed Records</p>
            <p className="text-lg font-medium text-gray-800">
              {(activation.recordsFailed || 0).toLocaleString()}
            </p>
          </div>

          {/* processingID */}
          <div>
            <p className="text-sm text-gray-500">Ad Account ID</p>
            <p className="text-lg font-medium text-gray-800">
              {activation.processingId}
            </p>
          </div>

          {/* Completed At */}
          <div>
            <p className="text-sm text-gray-500">Sent At</p>
            <p className="text-lg font-medium text-gray-800">
              {formatTimestamp(activation.startedAt)}
            </p>
          </div>

          {/* Completed At */}
          <div>
            <p className="text-sm text-gray-500">Completed At</p>
            <p className="text-lg font-medium text-gray-800">
              {formatTimestamp(activation.finishedAt)}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Status Icon
 * Shows one of three icons based on distribution status:
 * pending, failed, or completed.
 */
function DistributionStatus({
  value,
}: {
  value: 'pending' | 'failed' | 'completed';
}) {
  if (value === 'pending') {
    return (
      <span className="flex items-center gap-1 text-sm text-blue-light">
        <CgSpinner className="text-lg animate-spin" />
        <span>Processing</span>
      </span>
    );
  }

  if (value === 'failed') {
    return (
      <span className="flex items-center gap-1 text-sm text-orange">
        <MdOutlineErrorOutline className="text-lg" />
        <span>Failed</span>
      </span>
    );
  }

  return (
    <span className="flex items-center gap-1 text-sm text-green-light">
      <IoIosCheckmarkCircleOutline className="text-lg" />
      <span>Completed</span>
    </span>
  );
}
